import { useEffect, useMemo, useRef } from 'react';

import { useAppDispatch } from 'hooks/store';
import { ThunkGetCallScribeCall } from 'store/callScribe/thunks';
import { ICallScribeCallListItem } from 'services/callScribe/types';
import {
  needsReconcile,
  RECONCILE_MAX_FETCHES,
  RECONCILE_MS,
} from 'components/CallScribe/drawerReconcile';
import { startReconcilePoll } from 'components/CallScribe/reconcilePoll';
import { parseUtcInstant } from 'components/CallScribe/Recents/recentsHelpers';

export const RECONCILE_RECENT_WINDOW_MS = 30 * 60_000;

export const useReconcileRecentCalls = (
  calls: ICallScribeCallListItem[],
  openCallId: number | null,
): void => {
  const dispatch = useAppDispatch();
  const fetchedRef = useRef(new Map<number, number>());

  const recent = useMemo(() => {
    const now = Date.now();
    return calls.flatMap(call => {
      const created = parseUtcInstant(call.createdAtUtc);
      return call.id !== openCallId
        && needsReconcile(call)
        && created !== null
        && now - created.getTime() < RECONCILE_RECENT_WINDOW_MS
        ? [{ id: call.id, createdMs: created.getTime() }]
        : [];
    });
  }, [calls, openCallId]);
  const key = recent.map(entry => entry.id).join(',');
  const recentRef = useRef(recent);
  recentRef.current = recent;

  useEffect(() => {
    if (key === '') return undefined;
    return startReconcilePoll(() => {
      const now = Date.now();
      const due = recentRef.current.filter(entry =>
        now - entry.createdMs < RECONCILE_RECENT_WINDOW_MS
        && (fetchedRef.current.get(entry.id) ?? 0) < RECONCILE_MAX_FETCHES);
      if (due.length === 0) return 'stop';
      due.forEach(({ id }) => {
        fetchedRef.current.set(id, (fetchedRef.current.get(id) ?? 0) + 1);
        void dispatch(ThunkGetCallScribeCall(id));
      });
      return undefined;
    }, RECONCILE_MS);
  }, [key, dispatch]);
};
